import CoursePath from '@/models/CoursePath';
import { CoursesModelsType } from '@/utils/trees/TreeCourses';
import TreeItem from '@/components/shared/TreeItem';
import { mountKey } from '@/utils/courses';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useToggle from '@/data/hook/useToggle';
import CoursesTreeList from '@/components/shared/CoursesTreeList';
import CourseUnitTree from '@/components/unit/CourseUnitTree';
import TreeAddButton from '@/components/shared/TreeAddButton';
import { useTranslation } from 'react-i18next';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import { ROUTES } from '@/utils/routes';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';

type CoursePedTreeProps = {
  item: CoursePath;
  parentId: string;
};

export default function CoursePedTree({ item, parentId }: CoursePedTreeProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.tree',
  });

  const { navigate } = useCourseTreeNavigate();
  const type: CoursesModelsType = 'course';
  const { tree } = useTreeCourses();
  const { isOpen, toggle, open } = useToggle();
  const key = mountKey({ id: item.slug, type });
  const treeItem = { id: item.slug, name: item.course.abbreviation };

  const moduleNode = tree?.current.getParent(key);
  const lineNode = tree?.current.getParent(moduleNode?.id ?? '');

  const {
    results: lessonBlocks,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...lessonBlocksQueryKeys.list(item.slug, { pageSize: ORDENABLE_PAGE_SIZE })
      ._ctx.infinity,
    enabled: isOpen,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });
  return (
    <TreeItem
      objKey={key}
      type={type}
      parentId={parentId}
      item={treeItem}
      isLoading={isInitialLoading}
      onToggle={toggle}
      isOpen={isOpen}
      open={open}
    >
      <CoursesTreeList
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isInitialLoading}
        onReachEnd={fetchNextPage}
        hasNextPage={hasNextPage}
      >
        {lessonBlocks.map(lessonBlock => (
          <CourseUnitTree
            key={lessonBlock.id}
            item={lessonBlock}
            parentId={key}
            slug={item.slug}
          />
        ))}
        <TreeAddButton
          label={t('newUnit')}
          onClick={() => {
            navigate(
              ROUTES.ADMIN.COURSE_LINES.UNIT(
                lineNode?.realId ?? 0,
                moduleNode?.realId ?? 0,
                item.slug,
                'new',
              ),
            );
          }}
        />
      </CoursesTreeList>
    </TreeItem>
  );
}
